import React, {
    ReactNode,
    useEffect,
    useRef,
    useState,
    CSSProperties,
} from "react";
import classNames from "classnames";
import { createPortal } from "react-dom";

type DropdownPlacement = "top" | "bottom";

type DropdownPanelPosition = {
    top: number;
    left: number;
    maxHeight: number;
    placement: DropdownPlacement;
};

type DropdownWrapperProps = {
    wrapperClassName?: string;
    panelClassName?: string;
    children: ReactNode;
    renderTrigger: (params: {
        isOpen: boolean;
        toggle: () => void;
        close: () => void;
    }) => ReactNode;
    panelWidth?: number;
    panelBottomPadding?: number;
    panelSidePadding?: number;
    panelVerticalOffset?: number;
    panelMinHeight?: number;
    portalContainerId?: string;
    panelStyle?: CSSProperties;
};

export default function DropdownWrapper({
    wrapperClassName,
    panelClassName,
    children,
    renderTrigger,
    panelWidth = 260,
    panelBottomPadding = 16,
    panelSidePadding = 8,
    panelVerticalOffset = 8,
    panelMinHeight = 160,
    portalContainerId = "app-modal",
    panelStyle,
}: DropdownWrapperProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [panelPosition, setPanelPosition] =
        useState<DropdownPanelPosition | null>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);

    const toggle = () => setIsOpen((prev) => !prev);
    const close = () => setIsOpen(false);

    useEffect(() => {
        const onClickOutside = (e: MouseEvent) => {
            const target = e.target as Node;
            const isInsideTrigger = !!wrapperRef.current?.contains(target);
            const isInsidePanel = !!panelRef.current?.contains(target);

            if (!isInsideTrigger && !isInsidePanel) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", onClickOutside);
        return () => document.removeEventListener("mousedown", onClickOutside);
    }, []);

    useEffect(() => {
        if (!isOpen) {
            setPanelPosition(null);
            return;
        }

        const updatePanelPosition = () => {
            const triggerRect = wrapperRef.current?.getBoundingClientRect();
            if (!triggerRect) {
                return;
            }

            const viewportHeight =
                window.visualViewport?.height ?? window.innerHeight;
            const viewportWidth =
                window.visualViewport?.width ?? window.innerWidth;
            const spaceBelow =
                viewportHeight - triggerRect.bottom - panelBottomPadding;
            const spaceAbove = triggerRect.top - panelBottomPadding;

            const placement: DropdownPlacement =
                spaceBelow >= panelMinHeight || spaceBelow >= spaceAbove
                    ? "bottom"
                    : "top";

            const availableHeight =
                placement === "bottom" ? spaceBelow : spaceAbove;

            const maxHeight = Math.max(
                panelMinHeight,
                Math.floor(availableHeight - panelVerticalOffset)
            );

            const renderedPanelHeight = panelRef.current?.scrollHeight ?? 0;
            const panelHeight = Math.min(renderedPanelHeight, maxHeight);

            const maxLeft = viewportWidth - panelWidth - panelSidePadding;
            const left = Math.max(
                panelSidePadding,
                Math.min(Math.floor(triggerRect.left), Math.floor(maxLeft))
            );

            const top =
                placement === "bottom"
                    ? Math.floor(triggerRect.bottom + panelVerticalOffset)
                    : Math.floor(
                        Math.max(
                            panelBottomPadding,
                            triggerRect.top - panelHeight - panelVerticalOffset
                        )
                    );

            setPanelPosition({ top, left, maxHeight, placement });
        };

        updatePanelPosition();
        const frameRequest = window.requestAnimationFrame(updatePanelPosition);

        window.addEventListener("resize", updatePanelPosition);
        window.addEventListener("scroll", updatePanelPosition, true);

        return () => {
            window.cancelAnimationFrame(frameRequest);
            window.removeEventListener("resize", updatePanelPosition);
            window.removeEventListener("scroll", updatePanelPosition, true);
        };
    }, [
        isOpen,
        panelBottomPadding,
        panelMinHeight,
        panelSidePadding,
        panelVerticalOffset,
        panelWidth,
    ]);

    const panelContent = (
        <div
            className={classNames("dropdown__panel", panelClassName, {
                "dropdown__panel--top": panelPosition?.placement === "top",
                "dropdown__panel--bottom": panelPosition?.placement === "bottom",
                "dropdown__panel--visible": !!panelPosition,
                [`${panelClassName}--top`]:
                    !!panelClassName && panelPosition?.placement === "top",
                [`${panelClassName}--bottom`]:
                    !!panelClassName && panelPosition?.placement === "bottom",
            })}
            ref={panelRef}
            style={
                panelPosition
                    ? {
                        position: "fixed",
                        top: `${panelPosition.top}px`,
                        left: `${panelPosition.left}px`,
                        width: `${panelWidth}px`,
                        maxHeight: `${panelPosition.maxHeight}px`,
                        ...panelStyle,
                    }
                    : {
                        visibility: "hidden",
                        pointerEvents: "none",
                        ...panelStyle,
                    }
            }
        >
            {children}
        </div>
    );

    return (
        <div className={classNames("dropdown__wrapper", wrapperClassName)} ref={wrapperRef}>
            {renderTrigger({ isOpen, toggle, close })}
            {isOpen &&
                createPortal(
                    panelContent,
                    document.getElementById(portalContainerId) || document.body
                )}
        </div>
    );
}
